import { z } from "zod";
import { config } from "../../config/config";
import {
  planCategorySchema,
  planSchema,
  plansPaginationSchema,
} from "../../utils/zod/plan";
import type { Plan, PlanCategory } from "../../utils/zod/plan";
import { authHeaders, handleV2Error, offsetToPage } from "./_helpers";

export type { PlanCategory } from "../../utils/zod/plan";

export const getPlanCategoriesRequest = async (
  token: string,
): Promise<PlanCategory[]> => {
  const response = await fetch(`${config.API_V2_URL}/v2/plans/categories`, {
    method: "GET",
    headers: authHeaders(token),
  });

  if (!response.ok) await handleV2Error(response);

  const body = await response.json();
  return z.array(planCategorySchema).parse(body.data);
};

export type CreatePlanPayload = {
  name: string;
  categoryId: number;
  interval: Plan["interval"];
  durationDays: number;
  isCalendarDuration?: boolean;
  price: number;
  active?: boolean;
  createsSubscription?: boolean;
  registersAccessOnSale?: boolean;
  classCount?: number;
  requiredTagPublicIds?: string[];
};

const PLAN_ERROR_MESSAGES: Record<string, string> = {
  "Tag not found":
    "Una de las etiquetas elegidas ya no existe. Recarga la página y vuelve a elegirlas.",
};

const planErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  const errorData = await response.json().catch(() => ({}));
  const apiError: string | undefined = errorData.error;
  return (
    (apiError && PLAN_ERROR_MESSAGES[apiError]) ||
    apiError ||
    response.statusText ||
    fallback
  );
};

export const createPlanRequest = async (
  plan: CreatePlanPayload,
  token: string,
) => {
  const response = await fetch(`${config.API_URL}/plans`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(plan),
  });

  if (!response.ok) {
    throw new Error(await planErrorMessage(response, "Error al crear el plan"));
  }
  const data = await response.json();
  return data;
};

export type GetPlansParams = {
  search?: string;
  limit?: number;
  offset?: number;
  createsSubscription?: boolean;
  active?: boolean;
};

export type GetPlansResponse = {
  plans: Plan[];
  total: number;
  limit: number;
  offset: number;
};

const DEFAULT_LIMIT = 50;

export const getPlansRequest = async (
  token: string,
  params: GetPlansParams = {},
): Promise<GetPlansResponse> => {
  const limit = params.limit ?? DEFAULT_LIMIT;
  const offset = params.offset ?? 0;
  const page = offsetToPage(offset, limit);

  const qs = new URLSearchParams();
  if (params.search) qs.append("search", params.search);
  qs.append("limit", String(limit));
  qs.append("page", String(page));
  if (params.createsSubscription !== undefined)
    qs.append("createsSubscription", String(params.createsSubscription));
  if (params.active !== undefined) qs.append("active", String(params.active));

  const response = await fetch(
    `${config.API_V2_URL}/v2/plans?${qs.toString()}`,
    { method: "GET", headers: authHeaders(token) },
  );

  if (!response.ok) await handleV2Error(response);

  const body = await response.json();
  const plans = z.array(planSchema).parse(body.data);
  const pagination = plansPaginationSchema.parse(body.pagination);

  return {
    plans,
    total: pagination.totalCount,
    limit: pagination.perPage,
    offset: (pagination.currentPage - 1) * pagination.perPage,
  };
};

export const updatePlanRequest = async (
  publicId: string,
  plan: Partial<Plan>,
  token: string,
) => {
  const response = await fetch(`${config.API_URL}/plans/${publicId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(plan),
  });

  if (!response.ok) {
    throw new Error(
      await planErrorMessage(response, "Error al actualizar el plan"),
    );
  }

  const data = await response.json();
  return data;
};
